import { X, Phone, User, Clock, Info, Calendar, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useEffect } from "react";
import { format } from "date-fns";

// Types for the sidebar data
export interface VoiceCallDetailsData {
    id?: string;
    customer_name?: string | null;
    customer_number?: string | null;
    phone_number?: string | null;
    duration_seconds?: number | null;
    total_cost?: number | null;
    status?: string | null;
    call_type?: string | null;
    end_reason?: string | null;
    extracted_priority?: "low" | "medium" | "high" | "urgent" | null;
    extracted_sentiment?: "positive" | "neutral" | "negative" | "unknown" | null;
    intent?: string | null;
    next_step?: string | null;
    tags?: string[] | null;
    summary?: string | null;
    transcript?: string | null;
    recording_url?: string | null;
    started_at?: string | null;
    ended_at?: string | null;
    created_at?: string;
    transfer_destination?: string | null;
    voice_agent?: {
        name?: string;
        agent_name?: string;
        title?: string;
    } | null;
    // Form fields extracted from the call
    form_fields?: Array<{
        field_id: string;
        title: string;
        value: string;
        field_type?: string;
    }>;
}

interface VoiceCallDetailsSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    callData: VoiceCallDetailsData | null;
    isLoading?: boolean;
    title?: string;
}

// Helper function to format call duration
const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes === 0) {
        return `${remainingSeconds}s`;
    }
    
    return `${minutes}m ${remainingSeconds}s`;
};

export const VoiceCallDetailsSidebar = ({
    isOpen,
    onClose,
    callData,
    isLoading = false,
    title = "Call Details"
}: VoiceCallDetailsSidebarProps) => {
    console.log("VoiceCallDetailsSidebar render:", { isOpen, callData: !!callData, isLoading, title });
    
    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);
    
    if (!isOpen) return null;

    return (
        <>
            {/* Modal Backdrop */}
            <div 
                className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300"
                onClick={onClose}
                aria-hidden="true"
            />
            
            {/* Modal Content - Slide from right */}
            <div className={`
                fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50
                transform transition-transform duration-300 ease-in-out
                ${isOpen ? 'translate-x-0' : 'translate-x-full'}
                flex flex-col
            `}>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-white flex-shrink-0">
                    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="h-8 w-8 p-0 hover:bg-gray-100"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto">
                    {isLoading ? (
                        <div className="flex-1 flex items-center justify-center p-6">
                            <Spinner size="lg" className="text-indigo-600" />
                        </div>
                    ) : !callData ? (
                        <div className="flex-1 flex items-center justify-center p-6 text-center">
                            <div>
                                <Info className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                                <h3 className="text-lg font-medium mb-1 text-slate-800">No Call Selected</h3>
                                <p className="text-slate-500 text-sm max-w-md">
                                    Select a call to view details
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col h-full">
                            <div className="flex-1 overflow-auto min-h-0">
                                {/* Customer Info Section */}
                                <div className="p-6 border-b">
                                    {/* Customer Name */}
                                    <h2 className="text-[16px] font-semibold text-slate-900 mb-3">
                                        {callData.customer_name || "Unknown Customer"}
                                    </h2>
                                    
                                    {/* Contact Details */}
                                    <div className="space-y-2 mb-4">
                                        {/* Customer Phone */}
                                        <div className="flex items-center gap-2">
                                            <Phone className="h-4 w-4 text-slate-500" />
                                            <span className="text-sm text-slate-800">
                                                {callData.customer_number || <span className="text-gray-400">No phone number</span>}
                                            </span>
                                        </div>
                                        
                                        {/* Agent Name */}
                                        <div className="flex items-center gap-2">
                                            <User className="h-4 w-4 text-slate-500" />
                                            <span className="text-sm text-slate-800">
                                                {callData.voice_agent?.name || callData.voice_agent?.agent_name || "Unknown Agent"}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Status */}
                                    <div className="flex gap-2 mb-4">
                                        {callData.status && (
                                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize ${
                                                callData.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                callData.status === 'failed' ? 'bg-red-100 text-red-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {callData.status}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Call Analytics Section */}
                                {(callData.extracted_priority || callData.extracted_sentiment) && (
                                    <div className="p-6 border-b">
                                        <h3 className="text-sm font-semibold text-slate-900 mb-4">Call Analytics</h3>
                                        
                                        <div className="space-y-4">
                                            {/* Priority */}
                                            {callData.extracted_priority && (
                                                <div>
                                                    <p className="text-xs text-slate-500 mb-1">Priority</p>
                                                    <span className={`inline-block px-2 py-1 rounded-md text-xs font-medium capitalize ${
                                                        callData.extracted_priority === 'urgent' ? 'bg-red-100 text-red-800' :
                                                        callData.extracted_priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                                        callData.extracted_priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                                        callData.extracted_priority === 'low' ? 'bg-green-100 text-green-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {callData.extracted_priority}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Sentiment */}
                                            {callData.extracted_sentiment && callData.extracted_sentiment !== 'unknown' && (
                                                <div>
                                                    <p className="text-xs text-slate-500 mb-1">Sentiment</p>
                                                    <span className={`inline-block px-2 py-1 rounded-md text-xs font-medium capitalize ${
                                                        callData.extracted_sentiment === 'positive' ? 'bg-green-100 text-green-800' :
                                                        callData.extracted_sentiment === 'negative' ? 'bg-red-100 text-red-800' :
                                                        callData.extracted_sentiment === 'neutral' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {callData.extracted_sentiment}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Intent & Next Steps Section */}
                                {(callData.intent || callData.next_step || (callData.tags && callData.tags.length > 0)) && (
                                    <div className="p-6 border-b">
                                        <h3 className="text-sm font-semibold text-slate-900 mb-4">Call Intent & Actions</h3>
                                        
                                        <div className="space-y-4">
                                            {/* Intent */}
                                            {callData.intent && (
                                                <div>
                                                    <p className="text-xs text-slate-500 mb-2">Intent</p>
                                                    <p className="text-sm text-slate-800 bg-blue-50 rounded-md p-3 border border-blue-100">
                                                        {callData.intent}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Next Step */}
                                            {callData.next_step && (
                                                <div>
                                                    <p className="text-xs text-slate-500 mb-2">Recommended Next Step</p>
                                                    <p className="text-sm text-slate-800 bg-green-50 rounded-md p-3 border border-green-100">
                                                        {callData.next_step}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Tags */}
                                            {callData.tags && callData.tags.length > 0 && (
                                                <div>
                                                    <p className="text-xs text-slate-500 mb-2">Tags</p>
                                                    <div className="flex flex-wrap gap-1">
                                                        {callData.tags.map((tag, index) => (
                                                            <span 
                                                                key={index}
                                                                className="inline-block px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200"
                                                            >
                                                                {tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Call Summary Section */}
                                {callData.summary && (
                                    <div className="p-6 border-b">
                                        <h3 className="text-sm font-semibold text-slate-900 mb-3">Call Summary</h3>
                                        <p className="text-sm text-slate-700 leading-relaxed">
                                            {callData.summary}
                                        </p>
                                    </div>
                                )}

                                {/* Call Details Section */}
                                <div className="p-6 border-b">
                                    <h3 className="text-sm font-semibold text-slate-900 mb-4">Call Information</h3>
                                    
                                    <div className="space-y-4">
                                        {/* Duration */}
                                        <div>
                                            <p className="text-xs text-slate-500 mb-1">Duration</p>
                                            <div className="flex items-center gap-1">
                                                <Clock className="h-4 w-4 text-gray-400" />
                                                <span className="text-sm font-medium text-slate-800">
                                                    {formatDuration(callData.duration_seconds)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Cost */}
                                        {callData.total_cost && (
                                            <div>
                                                <p className="text-xs text-slate-500 mb-1">Cost</p>
                                                <div className="flex items-center gap-1">
                                                    <DollarSign className="h-4 w-4 text-gray-400" />
                                                    <span className="text-sm font-medium text-slate-800">
                                                        ${callData.total_cost.toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Started/Ended Times */}
                                        {callData.started_at && (
                                            <div>
                                                <p className="text-xs text-slate-500 mb-1">Started</p>
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="h-4 w-4 text-gray-400" />
                                                    <span className="text-sm text-slate-800">
                                                        {format(new Date(callData.started_at), 'MMM d, yyyy h:mm a')}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {callData.ended_at && (
                                            <div>
                                                <p className="text-xs text-slate-500 mb-1">Ended</p>
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="h-4 w-4 text-gray-400" />
                                                    <span className="text-sm text-slate-800">
                                                        {format(new Date(callData.ended_at), 'MMM d, yyyy h:mm a')}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* End Reason */}
                                        {callData.end_reason && (
                                            <div>
                                                <p className="text-xs text-slate-500 mb-1">End Reason</p>
                                                <span className="text-sm text-slate-800 capitalize">
                                                    {callData.end_reason.replace('_', ' ')}
                                                </span>
                                            </div>
                                        )}

                                        {/* Transfer Destination */}
                                        {callData.transfer_destination && (
                                            <div>
                                                <p className="text-xs text-slate-500 mb-1">Transferred To</p>
                                                <span className="text-sm text-slate-800">
                                                    {callData.transfer_destination}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Form Fields Section */}
                                {callData.form_fields && callData.form_fields.length > 0 && (
                                    <div className="p-6 border-b">
                                        <h3 className="text-sm font-semibold text-slate-900 mb-3">Extracted Information</h3>
                                        <div className="space-y-3">
                                            {callData.form_fields.map((field) => (
                                                <div key={field.field_id} className="bg-purple-50 rounded-md p-3 border border-purple-100">
                                                    <h4 className="text-xs font-medium text-purple-700 mb-1">{field.title}</h4>
                                                    <p className="text-sm text-slate-800 break-words">{field.value}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Transcript Section */}
                                {callData.transcript && (
                                    <div className="p-6 border-b">
                                        <h3 className="text-sm font-semibold text-slate-900 mb-3">Call Transcript</h3>
                                        <div className="bg-gray-50 rounded-md p-3 max-h-48 overflow-y-auto">
                                            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                                                {callData.transcript}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Recording Section */}
                                {callData.recording_url && (
                                    <div className="p-6">
                                        <h3 className="text-sm font-semibold text-slate-900 mb-3">Call Recording</h3>
                                        <audio controls className="w-full">
                                            <source src={callData.recording_url} type="audio/wav" />
                                            Your browser does not support the audio element.
                                        </audio>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}; 